import React, {
  useMemo,
  useRef,
  useState,
  useCallback,
  useEffect,
} from "react";
import {
  Box,
  Checkbox,
  Paper,
  Popper,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import ReusableGrid from "../../../components/ag grid/ReusableGrid";
import { BRAND } from "../../../components/auth/authTheme";

const ROW_HEIGHT = 48;
const HEADER_HEIGHT = 44;

const brandedCheckboxSx = {
  color: BRAND.border,
  "&.Mui-checked": { color: BRAND.orange },
  "&.Mui-disabled": { opacity: 0.35 },
};

const GRID_DEFAULT_COL_DEF = {
  sortable: false,
  resizable: false,
  minWidth: 100,
};

const ChallansTab = ({ challans, selectedChallan, setSelectedChallan }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const gridRef = useRef();
  const modalRef = useRef(null);
  const selectedRef = useRef(selectedChallan);
  const [anchorEl, setAnchorEl] = useState(null);
  const [description, setDescription] = useState("");
  const [isHoveringPopper, setIsHoveringPopper] = useState(false);
  const timeoutRef = useRef(null);

  selectedRef.current = selectedChallan;

  const rowCount = Math.max(Array.isArray(challans) ? challans.length : 0, 4);
  const gridHeight = ROW_HEIGHT * rowCount + HEADER_HEIGHT + 8;

  const handleChallanToggle = useCallback(
    (challanObj, checked) => {
      setSelectedChallan((prev) =>
        checked
          ? [...prev, challanObj]
          : prev.filter((c) => c.id !== challanObj.id)
      );
    },
    [setSelectedChallan]
  );

  useEffect(() => {
    if (!isMobile) {
      gridRef.current?.refreshCells({ force: true });
    }
  }, [selectedChallan, isMobile]);

  const handleMouseEnter = useCallback((event, desc) => {
    clearTimeout(timeoutRef.current);
    setAnchorEl(event.currentTarget);
    setDescription(desc);
  }, []);

  const handleMouseLeave = useCallback(() => {
    timeoutRef.current = setTimeout(() => {
      setAnchorEl(null);
      setDescription("");
    }, 100);
  }, []);

  const columnDefs = useMemo(
    () => [
      {
        headerName: "Select",
        field: "select",
        width: 72,
        maxWidth: 80,
        minWidth: 72,
        pinned: "left",
        suppressSizeToFit: true,
        sortable: false,
        headerClass: "permission-action-header",
        cellClass: "permission-action-cell",
        cellRenderer: (params) => (
          <Checkbox
            size="small"
            checked={selectedRef.current.some((c) => c.id === params.data.id)}
            onChange={(e) =>
              handleChallanToggle(params.data, e.target.checked)
            }
            sx={brandedCheckboxSx}
          />
        ),
      },
      {
        headerName: "Name",
        field: "name",
        minWidth: 200,
        flex: 2,
        cellRenderer: (params) => (
          <Typography
            variant="body2"
            onMouseEnter={(e) =>
              handleMouseEnter(e, params.data.description)
            }
            onMouseLeave={handleMouseLeave}
            sx={{
              cursor: params.data.description ? "help" : "default",
              color: params.data.description ? BRAND.orange : BRAND.text,
              fontWeight: 500,
              whiteSpace: "normal",
              wordBreak: "break-word",
              lineHeight: 1.4,
              "&:hover": params.data.description
                ? { color: BRAND.orangeHover }
                : undefined,
            }}
          >
            {params.data.name}
          </Typography>
        ),
      },
      {
        headerName: "Percentage",
        field: "percentage",
        width: 120,
        minWidth: 110,
        maxWidth: 140,
        suppressSizeToFit: true,
        cellClass: "permission-action-cell",
        valueFormatter: (params) => `${params.value || 0}%`,
      },
      {
        headerName: "Amount",
        field: "amount",
        width: 120,
        minWidth: 110,
        maxWidth: 140,
        suppressSizeToFit: true,
        cellClass: "permission-action-cell",
        valueFormatter: (params) => params.value || 0,
      },
    ],
    [handleChallanToggle, handleMouseEnter, handleMouseLeave]
  );

  // Helper to safely extract text from HTML if it is HTML
  const stripHtml = (html) => {
    if (!html) return "";
    const tmp = document.createElement("DIV");
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || "";
  };

  return (
    <Box ref={modalRef}>
      <Typography variant="body2" sx={{ color: BRAND.muted, mb: 1.5 }}>
        Select applicable statutory challans for this employee. Hover a name to
        view details.
      </Typography>

      {isMobile ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          {Array.isArray(challans) && challans.length > 0 ? (
            challans.map((challan) => {
              const isSelected = selectedRef.current.some((c) => c.id === challan.id);
              const displayValue = challan.percentage
                ? `${challan.percentage}%`
                : challan.amount
                ? challan.amount
                : "";
                
              const descText = stripHtml(challan.description);

              return (
                <Box
                  key={challan.id}
                  onClick={() => handleChallanToggle(challan, !isSelected)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    p: 1.5,
                    border: `1px solid ${BRAND.border}`,
                    borderRadius: "12px",
                    bgcolor: "#fff",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.02)",
                    cursor: "pointer",
                    transition: "all 0.2s",
                    ...(isSelected && {
                      borderColor: BRAND.orange,
                      bgcolor: "rgba(204, 85, 0, 0.03)",
                    }),
                  }}
                >
                  <Checkbox
                    checked={isSelected}
                    onChange={(e) => handleChallanToggle(challan, e.target.checked)}
                    onClick={(e) => e.stopPropagation()} // Prevent double toggle
                    sx={{ ...brandedCheckboxSx, mr: 1, p: 0.5 }}
                  />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontWeight: 700,
                        color: BRAND.navy,
                        fontSize: "0.95rem",
                      }}
                    >
                      {challan.name}
                    </Typography>
                    {descText && (
                      <Typography
                        sx={{
                          color: BRAND.muted,
                          fontSize: "0.75rem",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {descText}
                      </Typography>
                    )}
                  </Box>
                  {displayValue && (
                    <Typography
                      sx={{
                        fontWeight: 700,
                        color: BRAND.navy,
                        ml: 2,
                        fontSize: "0.9rem",
                      }}
                    >
                      {displayValue}
                    </Typography>
                  )}
                </Box>
              );
            })
          ) : (
            <Typography sx={{ color: BRAND.muted, textAlign: "center", py: 4 }}>
              No challans configured
            </Typography>
          )}
        </Box>
      ) : (
        <Box className="permission-grid-wrapper list-grid-container">
          <ReusableGrid
            rowData={challans}
            columnDefs={columnDefs}
            pagination={false}
            gridRef={gridRef}
            domLayout="normal"
            height={`${gridHeight}px`}
            defaultColDef={GRID_DEFAULT_COL_DEF}
            emptyMessage="No challans configured"
            emptyHint="Add challan types from Compliance settings first."
          />
        </Box>
      )}

      {!isMobile && (
        <Popper
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          placement="right-start"
          container={modalRef.current}
          sx={{ zIndex: 1400 }}
          modifiers={[
            {
              name: "preventOverflow",
              options: { boundary: modalRef.current },
            },
          ]}
        >
          <Paper
            onMouseEnter={() => setIsHoveringPopper(true)}
            onMouseLeave={() => {
              setIsHoveringPopper(false);
              setAnchorEl(null);
              setDescription("");
            }}
            sx={{
              p: 2,
              maxWidth: 320,
              maxHeight: 220,
              overflow: "auto",
              borderRadius: "12px",
              border: `1px solid ${BRAND.border}`,
              boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
              bgcolor: "#fff",
              "& h2": {
                fontSize: "0.9375rem",
                fontWeight: 600,
                color: BRAND.navy,
                m: 0,
                mb: 1,
              },
              "& li": {
                fontSize: "0.875rem",
                color: BRAND.muted,
                mb: 0.5,
              },
            }}
          >
            <div dangerouslySetInnerHTML={{ __html: description }} />
          </Paper>
        </Popper>
      )}
    </Box>
  );
};

export default ChallansTab;
